import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Target } from 'lucide-react';
import { toast } from '../notifications';
import {
  apiService,
  createLearningJournalGoal,
  updateLearningJournalGoal,
  deleteLearningJournalGoal
} from '../../services/api';
import Card from '../ui/Card.jsx';
import Modal from '../ui/Modal.jsx';
import EmptyState from '../ui/EmptyState.jsx';
import Skeleton from '../ui/Skeleton';
import GoalComposer from './GoalComposer';
import GoalRow from './GoalRow';
import { useAppMotion } from '../../hooks/useAppMotion';
import { haptic } from '../../utils/haptics';

// Erledigte Ziele stehen in derselben Liste (wie im Spot); ab dem sechsten
// klappt der Rest ein, damit die Liste ueber ein Schuljahr nicht endlos wird.
const VISIBLE_COMPLETED = 5;
// So lange bleibt ein gerade abgehaktes Ziel an seinem Platz, bevor es nach
// unten rutscht — sonst springt die Zeile weg, bevor man den Haken sieht.
const SETTLE_MS = 900;
const sortGoals = (goals) => [...goals].sort((a, b) => {
  if (a.completed !== b.completed) return a.completed ? 1 : -1;
  return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
});

/**
 * Ziele-Tab des Lernlogbuchs — eine Karte wie im Spot (Okt 2026, Motion-Kit
 * `ui.goalsCard`): Eingabe „Was willst du lernen?“, „2 von 5 Zielen erreicht“,
 * offene und erledigte Ziele in EINER Liste. Vorher lagen die erledigten hinter
 * einem eigenen „Erledigt (n)“-Umschalter in einer zweiten Karte.
 *
 * Eine flache Liste statt zweier Ziel-Arten.
 *
 * Ersetzt GoalsSection.js: kein Zieltyp-Toggle, kein Fortschritts-Slider (der
 * ohnehin nie gespeichert wurde), keine Kategorie/Priorität (existieren nicht im
 * Schema). Freie Ziele und Session-Ziele sehen identisch aus, der Unterschied
 * steht nur als Kontextzeile unter dem Zieltext.
 *
 * Lädt seine Daten selbst über GET /learning-journal/goals (ohne longTermOnly →
 * liefert beide Arten inkl. Session-Info) und ist damit unabhängig davon, ob der
 * User überhaupt schon Sessions abgeschlossen hat.
 */
const GoalsList = () => {
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const { layout } = useAppMotion();
  const settleTimer = useRef(null);
  useEffect(() => () => clearTimeout(settleTimer.current), []);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await apiService.getLearningJournalGoals();
        if (cancelled) return;
        if (response.success && Array.isArray(response.data?.goals)) {
          setGoals(sortGoals(response.data.goals));
        }
      } catch (error) {
        console.error('Fehler beim Laden der Lernziele:', error);
        if (!cancelled) toast.error('Lernziele konnten nicht geladen werden', { haptic: false });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const completedCount = useMemo(() => goals.filter(g => g.completed).length, [goals]);

  // Reihenfolge kommt aus `goals` (sortiert, ausser waehrend SETTLE_MS nach einem
  // Haken). Eingeklappt werden nur die aelteren Erledigten am Ende.
  const { visibleGoals, hiddenCount } = useMemo(() => {
    let seenCompleted = 0;
    const visible = [];
    let hidden = 0;
    goals.forEach((goal) => {
      if (goal.completed) {
        seenCompleted += 1;
        if (!showAllCompleted && seenCompleted > VISIBLE_COMPLETED) {
          hidden += 1;
          return;
        }
      }
      visible.push(goal);
    });
    return { visibleGoals: visible, hiddenCount: hidden };
  }, [goals, showAllCompleted]);

  const handleCreate = useCallback(async (text) => {
    // Optimistisch einfügen — kein Voll-Reload wie bisher
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      text,
      description: null,
      completed: false,
      isLongTerm: true,
      sessionId: null,
      tenant_sessions: null,
      createdAt: new Date().toISOString()
    };
    setGoals(prev => sortGoals([optimistic, ...prev]));

    try {
      const response = await createLearningJournalGoal({ text, isLongterm: true });
      if (!response.success || !response.data) throw new Error('Ziel konnte nicht angelegt werden');
      setGoals(prev => sortGoals(prev.map(g => (g.id === tempId ? { ...optimistic, ...response.data } : g))));
    } catch (error) {
      setGoals(prev => prev.filter(g => g.id !== tempId));
      console.error('Fehler beim Erstellen des Lernziels:', error);
      toast.error('Lernziel konnte nicht erstellt werden');
      throw error;
    }
  }, []);

  const handleToggle = useCallback(async (goal) => {
    const next = !goal.completed;
    // Erst abhaken (Haken + Strich sichtbar), dann einsortieren.
    setGoals(prev => prev.map(g => (g.id === goal.id ? { ...g, completed: next } : g)));
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => setGoals(prev => sortGoals(prev)), SETTLE_MS);

    try {
      // Der Endpoint behandelt session-bound UND freie Ziele — kein Fallback nötig
      await updateLearningJournalGoal(goal.id, { completed: next });
      // Nach der Serverantwort: Abhaken ist ein Erfolg, Zurücknehmen nur ein
      // Wertwechsel. Der Fehlerfall vibriert über toast.error.
      if (goal.completed) haptic?.selection?.();
      else haptic?.success?.();
    } catch (error) {
      setGoals(prev => sortGoals(prev.map(g => (g.id === goal.id ? { ...g, completed: goal.completed } : g))));
      console.error('Fehler beim Aktualisieren des Lernziels:', error);
      toast.error('Status konnte nicht geändert werden');
    }
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await deleteLearningJournalGoal(pendingDelete.id);
      setGoals(prev => prev.filter(g => g.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (error) {
      console.error('Fehler beim Löschen des Lernziels:', error);
      toast.error('Ziel konnte nicht gelöscht werden');
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDelete]);

  if (isLoading) {
    return (
      <Card className="space-y-3 p-4" aria-busy="true">
        <span className="sr-only">Lernziele werden geladen...</span>
        <div className="h-12 rounded-xl bg-surface-raised animate-pulse" aria-hidden="true" />
        {[0, 1, 2].map(i => (
          <div key={i} className="flex items-center gap-3" aria-hidden="true">
            <div className="h-6 w-6 rounded-full bg-chip animate-pulse" />
            <Skeleton.Line width="w-2/3" />
          </div>
        ))}
      </Card>
    );
  }

  return (
    <>
      <Card className="px-3.5 pb-1.5 pt-3.5 sm:px-[18px] sm:pt-[18px]">
        <GoalComposer onCreate={handleCreate} />

        {goals.length === 0 ? (
          <EmptyState
            icon={Target}
            size="sm"
            text="Noch keine Lernziele — schreib oben rein, was du dir vornimmst."
          />
        ) : (
          <>
            <p className="mx-0.5 mb-0.5 mt-3 text-meta text-muted-foreground">
              {completedCount} von {goals.length} {goals.length === 1 ? 'Ziel' : 'Zielen'} erreicht
            </p>
            <ul className="divide-y divide-border">
              {visibleGoals.map(goal => (
                <motion.li key={goal.id} layout={layout ? 'position' : false} transition={{ duration: 0.4 }}>
                  <GoalRow goal={goal} onToggle={handleToggle} onDelete={setPendingDelete} />
                </motion.li>
              ))}
            </ul>
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAllCompleted(true)}
                className="flex min-h-[44px] w-full items-center justify-center border-t border-border text-meta font-medium text-muted-foreground transition-colors duration-base hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              >
                Ältere erledigte anzeigen ({hiddenCount})
              </button>
            )}
          </>
        )}
      </Card>

      <Modal.Confirm
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Lernziel löschen?"
        confirmLabel="Löschen"
        variant="danger"
        busy={isDeleting}
      >
        <p className="text-sm text-muted-foreground">
          „{pendingDelete?.text}" wird dauerhaft entfernt.
        </p>
      </Modal.Confirm>
    </>
  );
};

export default GoalsList;
